import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { StatusCodes } from 'http-status-codes';
import { EMPTY } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  map,
  mergeMap,
  tap,
} from 'rxjs/operators';
import { getFromLocalStorage, setToLocalStorage } from 'wflow-cdk';
import { UserIdentityAPIService } from 'wflow-main-app/src/app/core/api/api/account/user-identity.service';
import { getOrg } from 'wflow-main-app/src/app/shared/utils/route.utils';
import * as MyOrganizationActions from './organizations.actions';
import {
  OrganizationsActionType,
  SetLastOrganization,
} from './organizations.actions';
import { OrganizationsStore } from './organizations.model';
import { selectCurrentOrganizationId } from './organizations.selectors';
import { LAST_ORGANIZATION_STORAGE_ID } from './organizations.state';

@Injectable()
export class OrganizationsEffects {
  setCurrent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActionType.SET_CURRENT),
      tap(
        (payload: { orgId: string }) =>
          !!payload.orgId &&
          setToLocalStorage(LAST_ORGANIZATION_STORAGE_ID, payload.orgId),
      ),
      filter((payload) => !!payload.orgId),
      map((payload) => SetLastOrganization(payload)),
    ),
  );

  getLast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActionType.GET_LAST),
      exhaustMap(() => this.store.select(selectCurrentOrganizationId)),
      map(
        (orgId) =>
          orgId ||
          `${getFromLocalStorage(LAST_ORGANIZATION_STORAGE_ID)}` ||
          getOrg(),
      ),
      map((orgId) => SetLastOrganization({ orgId })),
    ),
  );

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActionType.LOAD_LIST),
      mergeMap(() =>
        this.userService.getMyOrganizations().pipe(
          map((data) => MyOrganizationActions.LoadOrganizationsSuccess(data)),
          catchError((err) => {
            MyOrganizationActions.LoadOrganizationsError(err);

            if (err.status === StatusCodes.UNAUTHORIZED) {
              // If is UNAUTHORIZED don't need do any think, because it's solved in other (global) place
              return EMPTY;
            }

            throw err;
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<OrganizationsStore>,
    private userService: UserIdentityAPIService,
  ) {}
}
